import { router } from "@/router"
import { OrderedStepLocal } from "@/types/stepTypes"

/**
 * Preloads JS chunks for all routes a user could reach during this session.
 *
 * Service worker can only cache requests it sees. If a chunk is loaded for the first time
 * while the user is offline, the request fails and the route crashes. We trigger every
 * route chunk fetch on session entry so the SW precaches them while we still have network.
 */
export async function prefetchSessionRoutes(steps: Array<Pick<OrderedStepLocal, "name">>) {
  const routes = router.routesByPath

  const promises: Promise<unknown>[] = []

  const chunk = (path: keyof typeof routes) => {
    const route = routes[path]
    if (!route) return

    // loadRouteChunk returns undefined when the chunk is already loaded
    const promise = router.loadRouteChunk(route)
    if (promise) promises.push(promise)
  }

  // Always needed - intro flow + address + overview + finished + permissions guide
  chunk("/$sessionId/intro/")
  chunk("/$sessionId/intro/welcome")
  chunk("/$sessionId/intro/permissions")
  chunk("/$sessionId/intro/wall")
  chunk("/$sessionId/intro/wallDetail")
  chunk("/$sessionId/address")
  chunk("/$sessionId/overview/")
  chunk("/$sessionId/finished")
  chunk("/$sessionId/permissionsGuide")

  // Wall step
  if (steps.some((s) => s.name === "Wall")) {
    chunk("/$sessionId/wall/")
    chunk("/$sessionId/wall/$wallNumber/$wallId")
    chunk("/$sessionId/wall/$wallNumber/$wallId/wallDetail/$wallDetailId")
    chunk("/$sessionId/addNewWall")
  }

  // UtilityRoom step
  if (steps.some((s) => s.name === "UtilityRoom")) {
    chunk("/$sessionId/utilityRoom/")
    chunk("/$sessionId/utilityRoom/$amenityType")
  }

  // ExtraEquipment step
  if (steps.some((s) => s.name === "ExtraEquipment")) {
    chunk("/$sessionId/extraEquipment/")
    chunk("/$sessionId/extraEquipment/$amenityType")
  }

  await Promise.allSettled(promises)
}
